import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { IoClose } from "react-icons/io5";

import { BrlFormatCurrency } from "../../lib";

import styles from "./installmentsModal.module.css";

function totalOf(item) {
  if (item?.total !== undefined && item?.total !== null) return Number(item.total);
  return (
    Number(item?.installmentPrice || 0) * Number(item?.installmentNumber || 0)
  );
}

function hasInterestOf(item, baselineTotal) {
  if (typeof item?.hasInterest === "boolean") return item.hasInterest;
  if (item?.interest !== undefined && item?.interest !== null) {
    return Number(item.interest) > 0;
  }
  const total = totalOf(item);
  if (baselineTotal && total) {
    return total - baselineTotal > 0.01;
  }
  return false;
}

export default function InstallmentsModal({
  open,
  onClose,
  installments,
  selected,
  onSelect,
  title = "Deseja parcelar em quantas vezes?",
}) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  function handleBackdrop(e) {
    if (e.target === containerRef.current) onClose?.();
  }

  const aVista = installments.find((it) => Number(it.installmentNumber) === 1);
  const baselineTotal = aVista ? totalOf(aVista) : 0;

  return (
    <div
      ref={containerRef}
      className={styles.backdrop}
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Fechar"
          >
            <IoClose />
          </button>
        </div>

        <ul className={styles.list}>
          {installments.map((item) => {
            const n = Number(item.installmentNumber);
            const isActive = Number(selected) === n;
            const interest = hasInterestOf(item, baselineTotal);
            const label =
              n === 1
                ? "Pagamento à vista"
                : `${n}x de ${BrlFormatCurrency(item.installmentPrice)}`;
            return (
              <li key={n}>
                <button
                  type="button"
                  className={`${styles.item} ${isActive ? styles.itemActive : ""}`}
                  onClick={() => onSelect?.(n)}
                >
                  <span className={styles.radio}>
                    {isActive && <span className={styles.radioDot} />}
                  </span>
                  <div className={styles.itemBody}>
                    <div className={styles.itemLabel}>{label}</div>
                    {n > 1 && (
                      <div
                        className={`${styles.itemSubLabel} ${
                          interest ? styles.subWithInterest : styles.subNoInterest
                        }`}
                      >
                        {interest ? "Com juros" : "Sem juros"}
                      </div>
                    )}
                  </div>
                  <div className={styles.itemTotal}>
                    {BrlFormatCurrency(totalOf(item))}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

InstallmentsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  installments: PropTypes.array.isRequired,
  selected: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelect: PropTypes.func.isRequired,
  title: PropTypes.string,
};
